/**
 * /app/merchant/cotizaciones — Lista y gestión de cotizaciones merchant.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { DashboardHeader } from "@/components/DashboardHeader";
import { ClinicalPageShell } from "@/components/clinical";
import { PageLoader } from "@/components/PageLoader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Loader2, Shirt, CheckCircle2 } from "lucide-react";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { toast } from "sonner";
import type { MerchantQuoteWithItems } from "@/lib/db-service/merchant";
import { MerchantCotizacionesTour } from "@/components/ProductTour";

export const Route = createFileRoute("/app/merchant/cotizaciones")({
  component: CotizacionesPage,
  head: () => ({ meta: [{ title: "Cotizaciones — Structura Market" }] }),
});

// ─── STATUS ───────────────────────────────────────────────────────────────────

const STATUS_TABS = [
  { value: "", label: "Todas" },
  { value: "pending_quote", label: "Pendientes" },
  { value: "quoted", label: "Cotizadas" },
  { value: "approved", label: "Aprobadas" },
  { value: "expired", label: "Vencidas" },
  { value: "recotizar", label: "Recotizar" },
];

const STATUS_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  pending_quote: { label: "Pendiente", variant: "secondary" },
  quoted:        { label: "Cotizada",  variant: "default" },
  approved:      { label: "Aprobada",  variant: "default" },
  rejected:      { label: "Rechazada", variant: "destructive" },
  expired:       { label: "Vencida",   variant: "outline" },
  recotizar:     { label: "Recotizar", variant: "outline" },
};

// ─── DRAWER DE DETALLE ────────────────────────────────────────────────────────

function QuoteDrawer({
  quote,
  open,
  onClose,
  onSave,
  baseUrl,
}: {
  quote: MerchantQuoteWithItems;
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  baseUrl: string;
}) {
  const tid = useWorkspaceTenantId();
  const [price, setPrice] = useState(quote.quotedPrice?.toString() ?? "");
  const [quoteNotes, setQuoteNotes] = useState(quote.quoteNotes ?? "");
  const [leadTime, setLeadTime] = useState(quote.leadTimeDays?.toString() ?? "");
  const [saving, setSaving] = useState(false);

  const canQuote = quote.status === "pending_quote" || quote.status === "recotizar";

  async function handleSendQuote() {
    if (!price || isNaN(Number(price))) { toast.error("Ingresá un precio válido"); return; }
    setSaving(true);
    try {
      await apiSendJson(`/api/merchant/quotes/${quote.id}`, "PATCH", {
        status: "quoted",
        quotedPrice: Number(price),
        quoteNotes: quoteNotes || undefined,
        leadTimeDays: leadTime ? Number(leadTime) : undefined,
      }, tid);
      toast.success("Cotización enviada al cliente");
      onSave();
      onClose();
    } catch {
      toast.error("Error al guardar la cotización");
    } finally {
      setSaving(false);
    }
  }

  const publicLink = `${baseUrl}/cotizacion/${quote.token}`;
  const isExpired = quote.validUntil ? new Date(quote.validUntil).getTime() < Date.now() : false;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-tour="merchant-quote-detail">
        <SheetHeader className="mb-4">
          <SheetTitle>Solicitud #{quote.id.slice(-8).toUpperCase()}</SheetTitle>
          <Badge variant={STATUS_BADGE[quote.status]?.variant ?? "secondary"}>
            {STATUS_BADGE[quote.status]?.label ?? quote.status}
          </Badge>
        </SheetHeader>

        {/* Cliente */}
        <div className="space-y-1 mb-5">
          <p className="text-sm font-semibold text-gray-700">Cliente</p>
          <p className="text-sm text-gray-900">{quote.customerName}</p>
          <p className="text-sm text-gray-500">{quote.customerEmail}</p>
          {quote.customerPhone && <p className="text-sm text-gray-500">{quote.customerPhone}</p>}
        </div>

        {/* Producto */}
        <div className="mb-5">
          <p className="text-sm font-semibold text-gray-700 mb-1">Producto</p>
          <p className="text-sm text-gray-900">{quote.productName} × {quote.quantity}</p>
        </div>

        {/* Zonas */}
        {quote.items.length > 0 && (
          <div className="mb-5">
            <p className="text-sm font-semibold text-gray-700 mb-2">Zonas</p>
            <div className="space-y-2">
              {quote.items.map((item) => (
                <div key={item.id} className="flex items-start gap-3 bg-gray-50 rounded-lg p-3">
                  {item.fileUrl && (
                    <img
                      src={item.fileUrl}
                      alt={item.zoneName}
                      className="w-14 h-14 rounded object-cover border border-gray-200 shrink-0"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-800">{item.zoneName}</p>
                    <Badge variant="secondary" className="text-xs">{item.workTypeName}</Badge>
                    {item.description && <p className="text-xs text-gray-500 mt-1">{item.description}</p>}
                    {item.fileName && <p className="text-xs text-blue-600 mt-0.5">{item.fileName}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notas */}
        {quote.notes && (
          <div className="mb-5">
            <p className="text-sm font-semibold text-gray-700 mb-1">Notas del cliente</p>
            <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{quote.notes}</p>
          </div>
        )}

        {/* Link público */}
        <div className="mb-5">
          <p className="text-xs text-gray-400 mb-1">Link público para el cliente</p>
          <a
            href={publicLink}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-blue-600 hover:underline break-all"
          >
            {publicLink}
          </a>
        </div>

        {/* Cotización existente */}
        {quote.status === "quoted" && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5">
            <p className="text-sm font-semibold text-gray-700 mb-2">Cotización enviada</p>
            <p className="text-2xl font-black text-gray-900 mb-1">
              ${quote.quotedPrice?.toLocaleString("es-AR")}
            </p>
            {quote.validUntil && (
              <p className={`text-xs ${isExpired ? "text-red-600" : "text-gray-500"}`}>
                Vence: {new Date(quote.validUntil).toLocaleDateString("es-AR")}
                {isExpired && " (VENCIDA)"}
              </p>
            )}
          </div>
        )}

        {/* Form para cotizar */}
        {canQuote && (
          <div className="border-t border-gray-100 pt-5 space-y-4">
            <p className="text-sm font-semibold text-gray-800">
              {quote.status === "recotizar" ? "Nueva cotización" : "Enviar cotización"}
            </p>

            <div>
              <Label htmlFor="price" className="text-sm">Precio total *</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <Input
                  id="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="pl-6"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="leadTime" className="text-sm">Plazo de producción (días hábiles)</Label>
              <Input
                id="leadTime"
                type="number"
                min="1"
                value={leadTime}
                onChange={(e) => setLeadTime(e.target.value)}
                className="mt-1"
                placeholder="5"
              />
            </div>

            <div>
              <Label htmlFor="quoteNotes" className="text-sm">Comentarios para el cliente</Label>
              <Textarea
                id="quoteNotes"
                value={quoteNotes}
                onChange={(e) => setQuoteNotes(e.target.value)}
                rows={3}
                className="mt-1 resize-none"
                placeholder="Detalles adicionales, condiciones, etc."
              />
            </div>

            <Button
              onClick={handleSendQuote}
              disabled={saving}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-tour="merchant-quote-approve"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {quote.status === "recotizar" ? "Enviar nueva cotización" : "Enviar cotización al cliente"}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ─── PAGE ─────────────────────────────────────────────────────────────────────

function CotizacionesPage() {
  const tid = useWorkspaceTenantId();
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);
  const [quotes, setQuotes] = useState<MerchantQuoteWithItems[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("");
  const [selectedQuote, setSelectedQuote] = useState<MerchantQuoteWithItems | null>(null);

  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

  const load = useCallback(async () => {
    if (!tid) return;
    setLoading(true);
    try {
      const url = activeTab ? `/api/merchant/quotes?status=${activeTab}` : "/api/merchant/quotes";
      const data = await apiGetJson<MerchantQuoteWithItems[]>(url, tid);
      setQuotes(data);
    } catch (e) {
      const err = e as Error & { planLock?: PlanApiErrorPayload };
      if (err.planLock) { setLock(err.planLock); return; }
      toast.error("Error al cargar cotizaciones");
    } finally {
      setLoading(false);
    }
  }, [tid, activeTab]);

  useEffect(() => { void load(); }, [load]);

  if (!tid) return <PageLoader />;
  if (lock) return <FeatureLockedState payload={lock} />;

  return (
    <div>
      <DashboardHeader title="Cotizaciones" />
      <ClinicalPageShell offset="none">
      <MerchantCotizacionesTour />
      

      {/* Tabs de estado */}
      <div className="flex gap-1 flex-wrap mb-6">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setActiveTab(tab.value)}
            className={
              `px-3 py-1.5 rounded-lg text-sm font-medium transition-all ` +
              (activeTab === tab.value
                ? "bg-gray-900 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200")
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-gray-300" />
        </div>
      ) : quotes.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Shirt className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No hay cotizaciones en esta categoría.</p>
        </div>
      ) : (
        <div className="overflow-x-auto" data-tour="merchant-quotes-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400 uppercase tracking-wide border-b border-gray-100">
                <th className="pb-2 pr-4">Cliente</th>
                <th className="pb-2 pr-4">Producto</th>
                <th className="pb-2 pr-4">Zonas</th>
                <th className="pb-2 pr-4">Estado</th>
                <th className="pb-2 pr-4">Fecha</th>
                <th className="pb-2">Precio</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {quotes.map((q) => (
                <tr
                  key={q.id}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setSelectedQuote(q)}
                >
                  <td className="py-3 pr-4">
                    <p className="font-medium text-gray-900">{q.customerName}</p>
                    <p className="text-xs text-gray-400">{q.customerEmail}</p>
                  </td>
                  <td className="py-3 pr-4">
                    <p className="text-gray-800">{q.productName}</p>
                    <p className="text-xs text-gray-400">×{q.quantity}</p>
                  </td>
                  <td className="py-3 pr-4">
                    <div className="flex flex-wrap gap-1">
                      {q.items.slice(0, 3).map((item) => (
                        <Badge key={item.id} variant="secondary" className="text-xs">
                          {item.zoneName}
                        </Badge>
                      ))}
                      {q.items.length > 3 && (
                        <Badge variant="outline" className="text-xs">+{q.items.length - 3}</Badge>
                      )}
                    </div>
                  </td>
                  <td className="py-3 pr-4">
                    <Badge variant={STATUS_BADGE[q.status]?.variant ?? "secondary"}>
                      {STATUS_BADGE[q.status]?.label ?? q.status}
                    </Badge>
                  </td>
                  <td className="py-3 pr-4 text-gray-500 text-xs whitespace-nowrap">
                    {new Date(q.createdAt).toLocaleDateString("es-AR")}
                  </td>
                  <td className="py-3 text-gray-800 font-medium">
                    {q.quotedPrice != null ? `$${q.quotedPrice.toLocaleString("es-AR")}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedQuote && (
        <QuoteDrawer
          quote={selectedQuote}
          open={!!selectedQuote}
          onClose={() => setSelectedQuote(null)}
          onSave={load}
          baseUrl={baseUrl}
        />
      )}
    </ClinicalPageShell>
    </div>
  );
}
